import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AssetsService from '../../services/AssetsService';
import { Station, SyncLogEntry } from '../../services/models/assets';
import usePinchZoom, { PinchRange } from '../../components/Timeline/usePinchZoom';
import DateRangeFilter from './components/DateRangeFilter';
import ConnectivityTimeline from './components/ConnectivityTimeline';

type RangeKey = '1d' | '7d' | '30d' | '90d';

const rangeToDays: Record<RangeKey, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

// Cap rendered rows so a zoom redraw stays cheap; deep zoom-in refetches detail.
const MAX_CHART_POINTS = 1500;

const downsample = <T,>(rows: T[], cap: number): T[] => {
  if (rows.length <= cap) return rows;
  const step = (rows.length - 1) / (cap - 1);
  const out: T[] = [];
  for (let i = 0; i < cap; i += 1) out.push(rows[Math.round(i * step)]);
  return out;
};

const formatTimestamp = (value: number | string, hourly: boolean): string => {
  const date = new Date(value);
  if (hourly) {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
};

const formatUptime = (seconds?: number): string => {
  if (seconds == null) return '--';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
};

const metricInfo: Record<string, { label: string; description: string }> = {
  boot: {
    label: 'Démarrages',
    description: 'Nombre total de démarrages de la station depuis sa mise en service.',
  },
  crash: {
    label: 'Crashs',
    description: 'Nombre total de crashs logiciels détectés sur la station.',
  },
  mqtt_r: {
    label: 'Recovery MQTT',
    description: 'Nombre de reconnexions au broker MQTT après une perte de connexion.',
  },
  pdp_r: {
    label: 'Recovery PDP',
    description: 'Nombre de réinitialisations de la connexion data cellulaire (contexte PDP).',
  },
  full_r: {
    label: 'Recovery complet',
    description: 'Nombre de recovery complets de la stack réseau (MQTT + PDP).',
  },
  hard_r: {
    label: 'Hard reset',
    description: 'Nombre de redémarrages forcés du modem (dernier recours après échec des autres recovery).',
  },
  up: {
    label: 'Uptime',
    description: 'Temps écoulé depuis le dernier démarrage stable de la station.',
  },
  rl: {
    label: 'Dernier niveau',
    description: 'Dernier niveau de récupération réseau déclenché (0=aucun, 1=MQTT, 2=PDP, 3=complet, 4=hard reset).',
  },
};

const InfoTooltip: React.FC<{ text: string; children: ReactNode }> = ({ text, children }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1.5 bg-neutral rounded-md pointer-events-none z-10 w-56">
          <span className="block text-white typo-caption text-center leading-snug">{text}</span>
        </span>
      )}
    </span>
  );
};

const StationMonitoring = () => {
  const { siteId, stationId } = useParams<{ siteId: string; stationId: string }>();

  const [selectedRange, setSelectedRange] = useState<RangeKey>('7d');
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date } | null>(null);

  const { startDate, endDate } = useMemo(() => {
    if (customRange) {
      return { startDate: customRange.start, endDate: customRange.end };
    }
    const end = new Date();
    end.setSeconds(0, 0);
    const start = new Date();
    start.setDate(start.getDate() - rangeToDays[selectedRange]);
    start.setSeconds(0, 0);
    return { startDate: start, endDate: end };
  }, [selectedRange, customRange]);

  const handleRangeChange = useCallback((range: RangeKey) => {
    setSelectedRange(range);
    setCustomRange(null);
  }, []);

  const handleCustomRangeApply = useCallback((start: Date, end: Date) => {
    setCustomRange({ start, end });
  }, []);

  // Live view window during a pinch (visual only); commits a custom range on release.
  const [liveRange, setLiveRange] = useState<{ start: Date; end: Date } | null>(null);
  const chartsRef = useRef<HTMLDivElement>(null);

  const viewStartMs = (liveRange ? liveRange.start : startDate).getTime();
  const viewEndMs = (liveRange ? liveRange.end : endDate).getTime();
  const viewHourly = viewEndMs - viewStartMs <= 2 * 24 * 60 * 60 * 1000;

  const rangeRef = useRef<PinchRange>({ start: viewStartMs, end: viewEndMs });
  rangeRef.current = { start: viewStartMs, end: viewEndMs };

  const handlePinchChange = useCallback((r: PinchRange) => {
    setLiveRange({ start: new Date(r.start), end: new Date(r.end) });
  }, []);
  const handlePinchCommit = useCallback((r: PinchRange) => {
    setCustomRange({ start: new Date(r.start), end: new Date(r.end) });
    setLiveRange(null);
  }, []);

  usePinchZoom(chartsRef, {
    rangeRef,
    onChange: handlePinchChange,
    onCommit: handlePinchCommit,
    padding: 30,
  });

  const { data: station } = useQuery({
    queryKey: ['stations', siteId, stationId],
    queryFn: () => AssetsService.getStations(Number(siteId!)),
    select: (data): Station | undefined =>
      data.stations.find((s) => s.id === Number(stationId)),
  });

  const rangeQueryKey = customRange
    ? `custom-${startDate.toISOString()}-${endDate.toISOString()}`
    : selectedRange;

  const { data: syncHistory, isLoading } = useQuery({
    queryKey: ['station-sync-history', station?.token, rangeQueryKey],
    queryFn: () => AssetsService.getStationSyncHistory(station!.token, startDate, endDate),
    enabled: !!station?.token,
    select: (data) => data.logs,
    // Keep the current chart while a new range loads (no flash on zoom commit).
    placeholderData: keepPreviousData,
  });

  const { data: connectivity, isLoading: isConnectivityLoading } = useQuery({
    queryKey: ['station-connectivity', station?.token, rangeQueryKey],
    queryFn: () => AssetsService.getStationConnectivity(station!.token, startDate, endDate),
    enabled: !!station?.token,
    select: (data) => data.windows,
    placeholderData: keepPreviousData,
  });

  const chartData = useMemo(() => {
    const mapped = (syncHistory ?? []).map((entry: SyncLogEntry) => ({
      timestamp: new Date(entry.createdAt).getTime(),
      voltage: entry.voltage,
      rssi: entry.rssi,
      rsrp: entry.rsrp,
      sinr: entry.sinr,
      rsrq: entry.rsrq,
    }));
    return downsample(mapped, MAX_CHART_POINTS);
  }, [syncHistory]);

  const latestMetrics = station?.reliabilityMetrics;

  if (!station) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-neutral-40 typo-small">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1280px] px-4 flex flex-col gap-4 pb-8">
      <div className="flex items-center justify-between">
        <h1 className="text-neutral typo-h1">Monitoring</h1>
      </div>

      <DateRangeFilter
        selectedRange={selectedRange}
        onRangeChange={handleRangeChange}
        startDate={startDate}
        endDate={endDate}
        isCustomRange={customRange !== null}
        onCustomRangeApply={handleCustomRangeApply}
      />

      <div ref={chartsRef} style={{ touchAction: 'pan-y' }} className="flex flex-col gap-4">
      {/* Voltage chart */}
      <div className="p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
        <h3 className="text-neutral typo-h3">Voltage (V)</h3>
        <div className="h-[220px]">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-neutral-40 typo-small">Chargement...</span>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-neutral-40 typo-small">Aucune donnée sur la période sélectionnée.</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid stroke="rgba(26,26,26,0.05)" />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={[viewStartMs, viewEndMs]}
                  allowDataOverflow
                  tickCount={8}
                  minTickGap={32}
                  tickFormatter={(v) => formatTimestamp(v, viewHourly)}
                  tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
                  tickLine={false}
                  axisLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
                  tickLine={false}
                  axisLine={false}
                  width={50}
                  domain={['dataMin - 0.5', 'dataMax + 0.5']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    color: 'white',
                  }}
                  labelFormatter={(label) => new Date(Number(label)).toLocaleString('fr-FR')}
                  formatter={(value: any) => [`${value} V`, 'Voltage']}
                />
                <Line
                  type="monotone"
                  dataKey="voltage"
                  stroke="#FF8A4C"
                  strokeWidth={2}
                  dot={chartData.length > 120 ? false : { fill: '#FF8A4C', r: 2, strokeWidth: 0 }}
                  activeDot={{ fill: '#FF8A4C', r: 5, strokeWidth: 0 }}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Signal quality chart */}
      <div className="p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
        <h3 className="text-neutral typo-h3">Qualité signal (dBm)</h3>
        <div className="h-[260px]">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-neutral-40 typo-small">Chargement...</span>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-neutral-40 typo-small">Aucune donnée sur la période sélectionnée.</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid stroke="rgba(26,26,26,0.05)" />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={[viewStartMs, viewEndMs]}
                  allowDataOverflow
                  tickCount={8}
                  minTickGap={32}
                  tickFormatter={(v) => formatTimestamp(v, viewHourly)}
                  tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
                  tickLine={false}
                  axisLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
                  tickLine={false}
                  axisLine={false}
                  width={50}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    color: 'white',
                  }}
                  labelFormatter={(label) => new Date(Number(label)).toLocaleString('fr-FR')}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Line type="monotone" dataKey="rssi" name="RSSI" stroke="#FF8A4C" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                <Line type="monotone" dataKey="rsrp" name="RSRP" stroke="#22C55E" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                <Line type="monotone" dataKey="sinr" name="SINR" stroke="#3B82F6" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                <Line type="monotone" dataKey="rsrq" name="RSRQ" stroke="#A855F7" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      </div>

      {/* Connectivity / disconnection timeline */}
      <ConnectivityTimeline windows={connectivity} isLoading={isConnectivityLoading} />

      {/* Reliability metrics card */}
      <div className="p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
        <h3 className="text-neutral typo-h3">Métriques de fiabilité</h3>
        {latestMetrics ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.keys(metricInfo).map((key) => {
              const info = metricInfo[key];
              const value = (latestMetrics as any)[key];
              return (
                <div key={key} className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    <span className="text-neutral-60 typo-small">{info.label}</span>
                    <InfoTooltip text={info.description}>
                      <InformationCircleIcon className="w-3.5 h-3.5 text-neutral-40" />
                    </InfoTooltip>
                  </div>
                  <span className="text-neutral typo-body">
                    {key === 'up' ? formatUptime(value) : value ?? '--'}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-neutral-40 typo-small">Aucune métrique reçue pour le moment.</span>
        )}
      </div>
    </div>
  );
};

export default StationMonitoring;
